import {Injectable} from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpEvent, HttpErrorResponse, HttpResponse, HttpXsrfTokenExtractor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';

import {  throwError } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { AuthorizationService } from "../authorization/authorization.service";
import { ErrorLogService } from "../service/errorLogService";

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {

  private guestUrls = [
    'services/content/',
    'security/authenticate',
    'login/login',
    '/login/emailVerificationForLogin',
    'registration/',
    'guestpay/',
    'paynow/PayToggle',
    '/admin/',
    '/dynamiclease/getCounties',
    '/dynamiclease/getStates',
    'ccpa/',
    'util/calculator',
    'resetPassword/',
    'login/forgotUserId',
    'interestletter/adminretrieveInterestLetter',
    'errorlog/'
  ];

  constructor(
    private authService: AuthorizationService,
    private tokenExtractor: HttpXsrfTokenExtractor,
    private errorLogService: ErrorLogService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isErrorLogUrl = req.url.includes('errorlog');

    // ✅ Skip guest endpoints entirely for Bearer/auth token attachment
    if (this.isGuestUrl(req.url)) {
      return this.handleWithLogging(req, next, isErrorLogUrl);
    }

    const token = sessionStorage.getItem('Authorization');
    const userId = sessionStorage.getItem('userId');
    const csrfToken = this.tokenExtractor.getToken();

    if (csrfToken) {
      req = req.clone({
        setHeaders: { 'X-XSRF-TOKEN': csrfToken }
      });
    }

    // ✅ Attach token only if it exists
    if (token) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          userId: userId ?? ''
        }
      });
    }

    return this.handleWithLogging(req, next, isErrorLogUrl, token);
  }

  private handleWithLogging(
    req: HttpRequest<any>,
    next: HttpHandler,
    isErrorLogUrl: boolean,
    token?: string | null
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap((event: HttpEvent<any>) => {
        if (!isErrorLogUrl && event instanceof HttpResponse) {
          const body = event.body;
          if (
            body &&
            typeof body === 'object' &&
            body.statusCode &&
            body.statusCode !== '0000' &&
            body.statusCode !== 'SUCCESS'
          ) {
            const pageName = this.resolvePageName();
            const errMsg =
              body.statusMessage ||
              body.message ||
              `API returned error status: ${body.statusCode}`;
            const fieldName = this.extractEndpointName(req.url);
            this.errorLogService.logApiError(pageName, errMsg, fieldName);
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (!isErrorLogUrl) {
          const pageName = this.resolvePageName();
          const errorMsg = this.getServerErrorMessage(error);
          const fieldName = this.extractEndpointName(req.url);
          this.errorLogService.logApiError(pageName, errorMsg, fieldName);
        }

        // 🚫 DO NOT refresh token for guests
        if (!token) {
          return throwError(() => error);
        }

        // ✅ Token refresh only for authenticated users
        if (error.status === 401) {
          return this.authService.getAuthToken().pipe(
            mergeMap((authDetails: any) => {
              if (authDetails?.token) {
                sessionStorage.setItem('Authorization', authDetails.token);
                sessionStorage.setItem('userId', authDetails.userId);

                const retryReq = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${authDetails.token}`,
                    userId: authDetails.userId
                  }
                });

                return next.handle(retryReq);
              }

              return throwError(() => error);
            })
          );
        }

        return throwError(() => error);
      })
    );
  }

  private resolvePageName(): string {
    const url = this.router?.url || (typeof window !== 'undefined' ? window.location?.pathname : '') || '';
    if (url.includes('registrationConfirm')) return 'Registration Confirm';
    if (url.includes('registration')) return 'Registration';
    if (url.includes('admindashboard')) return 'Admin Dashboard';
    if (url.includes('dashboard')) return 'Dashboard';
    if (url.includes('login')) return 'Login';
    if (url.includes('manageYourProfile') || url.includes('manageprofile')) return 'Manage Profile';
    if (url.includes('guestpay')) return 'Guest Pay';
    if (url.includes('incidentManagement')) return 'Incident Management';
    return sessionStorage.getItem('page') || 'General';
  }

  private extractEndpointName(url: string): string {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1] || url;
  }

  private getServerErrorMessage(error: HttpErrorResponse): string {
    if (error.error instanceof ErrorEvent) {
      return `Client Error: ${error.error.message}`;
    }

    if (error.error && typeof error.error === 'object' && (error.error.message || error.error.statusMessage)) {
      return error.error.message || error.error.statusMessage;
    }

    if (typeof error.error === 'string' && error.error.trim()) {
      return error.error;
    }

    switch (error.status) {
      case 400:
        return `Bad Request (400): ${error.message}`;
      case 401:
        return `Unauthorized (401): ${error.message}`;
      case 403:
        return `Forbidden (403): ${error.message}`;
      case 404:
        return `Not Found (404): ${error.message}`;
      case 500:
        return `Internal Server Error (500): ${error.message}`;
      default:
        return `HTTP ${error.status || 'Unknown'}: ${error.message || 'Server Error'}`;
    }
  }

  private isGuestUrl(url: string): boolean {
    return this.guestUrls.some(g => url.includes(g));
  }
}

